/**
 * AvatarIdle: 待機中のアバター表示
 * - ベース: 元画像そのまま固定
 * - 頭レイヤー: 同じ画像をclip-pathで頭部分だけ切り出し、上に重ねてアニメーション
 * - 目: オーバーレイで瞬き
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { preloadImageInfo, preloadImageUrl } from "../lib/imagePreload";

interface AvatarIdleProps {
  imageUrl: string;
  eyesHalfBlinkUrl: string;
  eyesBlinkUrl: string;
  suppressBlink?: boolean;
  /** clip-pathの下端（%）。これより上が頭として動く。デフォルト: 68% */
  headClipBottom?: number;
  eyePosition?: { top: string; left: string; width: string; height: string };
  eyeMotion?: {
    maskImage?: string;
    maskPosition: string;
    maskSize: string;
    maxOffsetXPercent: number;
    maxOffsetYPercent: number;
    intervalMinMs?: number;
    intervalMaxMs?: number;
    transitionMs?: number;
    centerReturnChance?: number;
    opacity?: number;
  };
  browMotion?: {
    maskImage: string;
    translateYPercent: number;
    transitionMs?: number;
    opacity?: number;
  };
  /** 口ありの画像URL（jawレイヤーで重ねる）。省略時はimageUrlをそのまま使う */
  mouthImageUrl?: string;
  /** jawマスクURL。mouthImageUrlと併用 */
  jawMaskUrl?: string;
  animateHead?: boolean;
  alt?: string;
}

type BlinkPhase = "open" | "half_closing" | "closed" | "half_opening";

const BLINK_TIMING = {
  half_closing: 30,
  closed: 90,
  half_opening: 30,
};
const HEAD_ANIMATION_DURATION_MS = 6000;
const HEAD_MOTION_MASK =
  "radial-gradient(ellipse 26% 43% at 50% 45%, black 64%, rgba(0,0,0,0.72) 80%, transparent 100%)";

function computeHeadAnimationDelayMs() {
  if (typeof performance === "undefined") return 0;
  return -Math.floor(performance.now() % HEAD_ANIMATION_DURATION_MS);
}

const AvatarIdle: React.FC<AvatarIdleProps> = ({
  imageUrl,
  eyesHalfBlinkUrl,
  eyesBlinkUrl,
  suppressBlink = false,
  headClipBottom = 68,
  eyePosition = {
    top: "37.8%",
    left: "39.8%",
    width: "16.25%",
    height: "5.4%",
  },
  mouthImageUrl,
  jawMaskUrl,
  animateHead = true,
  alt = "avatar",
}) => {
  const [headAnimationDelayMs, setHeadAnimationDelayMs] = useState(() => computeHeadAnimationDelayMs());
  const [fullSizeBlink, setFullSizeBlink] = useState(false);
  const timerRef = useRef<number>(0);
  const blinkPhaseRef = useRef<BlinkPhase>("open");
  const blinkResumeAtRef = useRef<number>(0);
  const blinkSequenceRef = useRef<number>(0);
  const halfBlinkRef = useRef<HTMLImageElement | null>(null);
  const closedBlinkRef = useRef<HTMLImageElement | null>(null);

  const applyBlinkPhase = useCallback((phase: BlinkPhase) => {
    blinkPhaseRef.current = phase;

    const halfBlinkOpacity =
      phase === "half_closing" || phase === "half_opening"
        ? 1
        : phase === "closed"
        ? 0.32
        : 0;
    const closedBlinkOpacity = phase === "closed" ? 1 : 0;

    if (halfBlinkRef.current) {
      halfBlinkRef.current.style.opacity = String(halfBlinkOpacity);
    }
    if (closedBlinkRef.current) {
      closedBlinkRef.current.style.opacity = String(closedBlinkOpacity);
    }
  }, []);

  const doSingleBlink = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const sequence = ++blinkSequenceRef.current;
      const isActive = () => blinkSequenceRef.current === sequence;
      applyBlinkPhase("half_closing");
      setTimeout(() => {
        if (!isActive()) {
          resolve();
          return;
        }
        applyBlinkPhase("closed");
        setTimeout(() => {
          if (!isActive()) {
            resolve();
            return;
          }
          applyBlinkPhase("half_opening");
          setTimeout(() => {
            if (!isActive()) {
              resolve();
              return;
            }
            applyBlinkPhase("open");
            resolve();
          }, BLINK_TIMING.half_opening);
        }, BLINK_TIMING.closed);
      }, BLINK_TIMING.half_closing);
    });
  }, [applyBlinkPhase]);

  const doBlink = useCallback(() => {
    const isDouble = Math.random() < 0.3;
    doSingleBlink().then(() => {
      if (isDouble && Date.now() >= blinkResumeAtRef.current) {
        setTimeout(() => {
          if (Date.now() >= blinkResumeAtRef.current) doSingleBlink();
        }, 100);
      }
    });
  }, [doSingleBlink]);

  useEffect(() => {
    const now = Date.now();
    if (suppressBlink) {
      blinkSequenceRef.current += 1;
      blinkResumeAtRef.current = now + 360;
      applyBlinkPhase("open");
      return;
    }

    blinkResumeAtRef.current = Math.max(blinkResumeAtRef.current, now + 260);
  }, [applyBlinkPhase, suppressBlink]);

  useEffect(() => {
    if (animateHead) {
      setHeadAnimationDelayMs(computeHeadAnimationDelayMs());
    }
  }, [animateHead]);

  useEffect(() => {
    let cancelled = false;
    setFullSizeBlink(false);
    preloadImageInfo(eyesHalfBlinkUrl).then((imageInfo) => {
      if (!cancelled) {
        setFullSizeBlink((imageInfo?.naturalWidth ?? 0) > 500);
      }
    });
    void preloadImageUrl(eyesBlinkUrl);

    const scheduleBlink = (delayOverride?: number) => {
      const delay = delayOverride ?? (2500 + Math.random() * 4000);
      timerRef.current = window.setTimeout(() => {
        if (Date.now() < blinkResumeAtRef.current) {
          scheduleBlink(220);
          return;
        }

        doBlink();
        scheduleBlink();
      }, delay);
    };
    scheduleBlink();
    return () => {
      cancelled = true;
      window.clearTimeout(timerRef.current);
    };
  }, [eyesHalfBlinkUrl, eyesBlinkUrl, doBlink]);

  const headAnimationStyle = animateHead
    ? { animationDelay: `${headAnimationDelayMs}ms` }
    : undefined;

  return (
    <div className="w-full h-full relative overflow-hidden">
      {/* ベース画像（固定） */}
      <img
        src={imageUrl}
        alt={alt}
        className="w-full h-full object-cover absolute inset-0"
      />

      {/* 頭レイヤー（clip-pathで切り出し+アニメーション） */}
      <img
        src={imageUrl}
        alt=""
        className={`w-full h-full object-cover absolute inset-0 ${animateHead ? "avatar-head" : ""}`}
        style={{
          maskImage: HEAD_MOTION_MASK,
          WebkitMaskImage: HEAD_MOTION_MASK,
          ...headAnimationStyle,
        }}
      />

      {/* 瞬きはhalfとclosedを重ねて、紙芝居感を少し減らす */}
      {eyesHalfBlinkUrl && eyesBlinkUrl && (
        <div
          className={`absolute inset-0 ${animateHead ? "avatar-head" : ""}`}
          style={{
            pointerEvents: "none",
            ...(animateHead
              ? {
                  maskImage: HEAD_MOTION_MASK,
                  WebkitMaskImage: HEAD_MOTION_MASK,
                  ...headAnimationStyle,
                }
              : {}),
          }}
        >
          <img
            ref={halfBlinkRef}
            src={eyesHalfBlinkUrl}
            alt=""
            className={`absolute ${fullSizeBlink ? "inset-0 w-full h-full object-cover" : ""}`}
            style={{
              ...(fullSizeBlink ? {} : {
                top: eyePosition.top,
                left: eyePosition.left,
                width: eyePosition.width,
                height: eyePosition.height,
                objectFit: "fill",
              }),
              opacity: 0,
              transition: "opacity 30ms ease-out",
            }}
          />
          <img
            ref={closedBlinkRef}
            src={eyesBlinkUrl}
            alt=""
            className={`absolute ${fullSizeBlink ? "inset-0 w-full h-full object-cover" : ""}`}
            style={{
              ...(fullSizeBlink ? {} : {
                top: eyePosition.top,
                left: eyePosition.left,
                width: eyePosition.width,
                height: eyePosition.height,
                objectFit: "fill",
              }),
              opacity: 0,
              transition: "opacity 30ms ease-out",
            }}
          />
        </div>
      )}
      {/* jawレイヤー: 口ありの画像をjawマスクで重ねる */}
      {mouthImageUrl && jawMaskUrl && (
        <img
          src={mouthImageUrl}
          alt=""
          className={`w-full h-full object-cover absolute inset-0 ${animateHead ? "avatar-head" : ""}`}
          style={{
            maskImage: `url(${jawMaskUrl})`,
            WebkitMaskImage: `url(${jawMaskUrl})`,
            maskMode: "luminance",
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",
            maskSize: "100% 100%",
            WebkitMaskSize: "100% 100%",
            maskPosition: "center",
            WebkitMaskPosition: "center",
            pointerEvents: "none",
            ...headAnimationStyle,
          }}
        />
      )}
    </div>
  );
};

export default AvatarIdle;
